<template>
  <v-container fluid>
    <ActionBar
      :selectedItems="selectedItems"
      :pagination="pagination"
      @change="updatePage"
      @reprocessThisInvoice="handleReprocessThisInvoice"
      @reprocessAllDeclinedInvoices="handleReprocessAllDeclinedInvoices"
      @reprocessSelectedInvoices="handleReprocessSelectedInvoices"
    />

    <div
      :class="[`${
        isFailed || !isValidArray(items)
          ? 'page__content--no-items'
          : 'page__content'
      }`, {
        'page__content--from-v4': noMainMenuFromV4
      }]"
    >
      <Filters
        :status="currentStatus"
        @change="updateFilters"
      />

      <Table
        :items="items"
        :pagination="pagination"
        :status="currentStatus"
        :clearSelectedItems="clearSelectedItems"
        @reloadData="reloadData"
        @goToPage="updatePage"
        @selectedItems="updateSelectedItems"
        @reprocessThisInvoice="handleReprocessThisInvoice"
      />
    </div>

    <ReprocessAllDeclinedInvoicesDialog
      :visible="reprocessAllDeclinedInvoicesDialog"
      :item="selectedItem"
      :filters="filters"
      @close="closeReprocessAllDeclinedInvoicesDialog"
    />
  </v-container>
</template>

<script>
import moment from 'moment'
import { mapState, mapActions } from 'vuex'
import { STATUS_IS_LOADING, STATUS_SUCCESS, STATUS_FAILED } from 'utils/constants'
import { isArray } from 'utils/functions'
import ActionBar from 'components/manualRetrieval/ActionBar'
import Filters from 'components/manualRetrieval/Filters'
import Table from 'components/manualRetrieval/Table'
import ReprocessAllDeclinedInvoicesDialog from 'components/manualRetrieval/dialogs/ReprocessAllDeclinedInvoicesDialog'

export default {
  name: 'ManualRetrieval',
  components: {
    ActionBar,
    Filters,
    Table,
    ReprocessAllDeclinedInvoicesDialog
  },
  computed: {
    ...mapState({
      mainMenuIsOpen: ({ mainMenu }) => mainMenu.isOpen,
      selectedCompany: ({ company }) => company.selectedCompany,
      claims: ({ userClaims }) => userClaims.claims,
      noMainMenuFromV4: ({ mainMenu }) => mainMenu.noMainMenuFromV4
    }),

    isLoading () {
      return this.currentStatus === STATUS_IS_LOADING
    },

    isSuccess () {
      return this.currentStatus === STATUS_SUCCESS
    },

    isFailed () {
      return this.currentStatus === STATUS_FAILED
    }
  },
  data () {
    return {
      namespace: 'Views.ManualRetrieval',
      pagination: {
        totalElements: 0,
        totalPages: 0,
        currentPage: 1,
        itemsPerPage: 25
      },
      filters: {
        companyId: null,
        startDate: null,
        endDate: null
      },
      currentStatus: null,
      items: [],
      selectedItem: null,
      selectedItems: [],
      clearSelectedItems: false,
      reprocessAllDeclinedInvoicesDialog: false
    }
  },

  methods: {
    ...mapActions({
      showAlert: 'snackbar/show'
    }),

    isValidArray (val) {
      return isArray(val)
    },

    updatePagination (response, itemsPerPage) {
      const totalPages = response.totalPages

      this.pagination = {
        ...this.pagination,
        totalElements: response.totalElements,
        totalPages
      }
    },

    resetPagination () {
      this.pagination = {
        totalElements: 0,
        totalPages: 0,
        currentPage: 1,
        itemsPerPage: 25
      }
    },

    updateFilters (filters) {
      this.filters = { ...filters }
      this.reloadData()
    },

    updatePage (page) {
      scrollTo(0, 0)

      this.pagination = {
        ...this.pagination,
        currentPage: page
      }

      this.loadData()
    },

    updateSelectedItems (items) {
      this.selectedItems = items
    },

    handleClearSelectedItems () {
      this.clearSelectedItems = !this.clearSelectedItems
    },

    formatDate (str, type = 'start') {
      const YYYY_MM_DD = /^\d{4}-\d{2}-\d{2}$/
      const YYYY_MM_DD_HH_MM = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/
      const YYYY_MM_DD_HH_MM_SS = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/

      if (YYYY_MM_DD.test(str) && type === 'start') return `${str}T00:00:00-0300`
      if (YYYY_MM_DD.test(str) && type === 'end') return `${str}T23:59:59-0300`

      if (YYYY_MM_DD_HH_MM.test(str) && type === 'start') return `${str}:00-0300`
      if (YYYY_MM_DD_HH_MM.test(str) && type === 'end') return `${str}:59-0300`

      if (YYYY_MM_DD_HH_MM_SS.test(str) && type === 'start') return `${str}-0300`
      if (YYYY_MM_DD_HH_MM_SS.test(str) && type === 'end') return `${str}-0300`

      return str
    },

    async loadData () {
      if (this.currentStatus === STATUS_IS_LOADING) return

      this.currentStatus = STATUS_IS_LOADING

      try {
        const { currentPage, itemsPerPage } = this.pagination
        const { companyId, startDate, endDate } = this.filters

        const today = new Date()
        const day = String(today.getDate()).padStart(2, '0')
        const daysInMonth = moment().daysInMonth()
        const days = Number(day) < daysInMonth ? day : daysInMonth

        const { data: res } = await this.$apiService.getReprocessCreditCardInvoices(
          { page: currentPage - 1 },
          { limit: itemsPerPage },
          { companyIds: companyId },
          { startDate: startDate ? this.formatDate(startDate, 'start') : moment().format('YYYY-MM-') + '01T00:00:00-0300' },
          { endDate: endDate ? this.formatDate(endDate, 'end') : moment().format('YYYY-MM-') + days + 'T23:59:59-0300' }
        )

        if (!res.valid) throw new Error()

        this.items = res.content || []
        this.updatePagination(res, itemsPerPage)
        this.handleClearSelectedItems()
        this.currentStatus = STATUS_SUCCESS
      } catch (err) {
        console.log('ERROR:', err)
        this.currentStatus = STATUS_FAILED
      }
    },

    reloadData () {
      this.resetPagination()
      this.loadData()
      this.handleClearSelectedItems()
    },

    openReprocessAllDeclinedInvoicesDialog () {
      this.reprocessAllDeclinedInvoicesDialog = true
    },

    closeReprocessAllDeclinedInvoicesDialog (reload) {
      this.reprocessAllDeclinedInvoicesDialog = false

      if (reload) {
        this.loadData()
        this.handleClearSelectedItems()
      }
    },

    async handleReprocessThisInvoice (val) {
      this.currentStatus = STATUS_IS_LOADING

      try {
        const orderIds = this.isValidArray(this.selectedItems) ? this.selectedItems : [val]
        await this.$apiService.reprocessCreditCardInvoiceMassive({ orderIds })

        this.showAlert({
          type: 'success',
          message: this.$t(`${this.namespace}.reprocessThisInvoiceSuccessMessage`)
        })

        this.reloadData()
        this.currentStatus = STATUS_SUCCESS
      } catch (err) {
        console.log('ERROR:', err)
        this.showAlert({
          type: 'error',
          message: this.$t(`${this.namespace}.reprocessThisInvoiceErrorMessage`)
        })

        this.currentStatus = STATUS_FAILED
      }
    },

    handleReprocessAllDeclinedInvoices () {
      this.openReprocessAllDeclinedInvoicesDialog()
    },

    async handleReprocessSelectedInvoices () {
      try {
        await this.handleReprocessThisInvoice()

        this.showAlert({
          type: 'success',
          message: this.$t(`${this.namespace}.reprocessSelectedInvoicesSuccessMessage`)
        })
      } catch (err) {
        console.log('ERROR:', err)
        this.showAlert({
          type: 'error',
          message: this.$t(`${this.namespace}.reprocessSelectedInvoicesErrorMessage`)
        })
      }

      this.reloadData()
    }
  },

  watch: {
    selectedCompany () {
      this.reloadData()
    }
  },

  mounted () {
    this.loadData()
  }
}
</script>
